// Auth context. Real Firebase-backed implementation.

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { auth } from "../config/firebase_config";
import { User } from "firebase/auth";
import { getUser } from "../api/users";
import { setApiToken } from "../api/client";

interface AuthUser {
  userId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  emailVerified: boolean;
  phoneNumber: string;
  role: string;
  typeUser: string;
  joinDate: any;
  photoURL: string;
  accessToken: string;
}

interface AuthContextType {
  isLoading: boolean;
  isLoggedIn: boolean;
  currentUser: AuthUser;
  userRole: string | null;
}

const defaultUser: AuthUser = {
  userId: null,
  email: "",
  firstName: "",
  lastName: "",
  fullName: "",
  emailVerified: false,
  phoneNumber: "",
  role: "",
  typeUser: "",
  joinDate: null,
  photoURL: "",
  accessToken: "",
};

export const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  isLoggedIn: false,
  currentUser: defaultUser,
  userRole: null,
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<AuthUser>(defaultUser);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      setIsLoading(true);

      if (user) {
        try {
          const accessToken = await user.getIdToken();
          setApiToken(accessToken);
          const userData = await getUser(user.uid, accessToken);
          if (userData && userData.typeUser) {
            setIsLoggedIn(true);
            setCurrentUser({
              userId: user.uid,
              email: user.email || "",
              firstName: userData.firstName || "",
              lastName: userData.lastName || "",
              fullName: `${userData.firstName} ${userData.lastName}` || "",
              emailVerified: user.emailVerified,
              phoneNumber: userData.phoneNumber || "",
              role: userData.role,
              typeUser: userData.typeUser,
              joinDate: userData.joinDate || null,
              photoURL: userData.photoURL || "",
              accessToken,
            });
            setUserRole(userData.role);
          } else {
            // Backend returned no usable user record — treat as logged out
            setApiToken("");
            setCurrentUser(defaultUser);
            setIsLoggedIn(false);
            setUserRole(null);
          }
        } catch (error) {
          console.error("Auth context: failed to fetch user record", error);
          setApiToken("");
          setCurrentUser(defaultUser);
          setIsLoggedIn(false);
          setUserRole(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setApiToken("");
        setCurrentUser(defaultUser);
        setIsLoading(false);
        setIsLoggedIn(false);
        setUserRole(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const value = { currentUser, isLoading, isLoggedIn, userRole };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
